import { ChildMachineSchema, ChildEvents, MediaMachineContext } from "./types";
import { Machine, sendParent } from "xstate";
import * as actions from "./mediaactions";

const isText = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type === "text";

const isAV = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type !== "text" && ctx.mmo.type !== "shape";

const isAVOrShape = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type !== "text" && ctx.mmo.type !== "audio";

const isShape = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type === "shape";

const isVideo = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type === "video";

const isGallery = (ctx: MediaMachineContext, _: ChildEvents) =>
  ctx.mmo.type === "gallery";

const isValidPos = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (event.type === "SET_POSITION") {
    // console.log(`[isValidPos] CALLED! MMO on screen ${ctx.screenIndex}`);
    // console.log(`[isValidPos] Position: x=${event.x}, y=${event.y}`);
    // console.log(`[isValidPos] ViewSize:`, ctx.viewSize);
    // console.log(`[isValidPos] viewSize.width:`, ctx.viewSize.width);
    // console.log(`[isValidPos] viewSize.height:`, ctx.viewSize.height);

    const isValid = (
      event.type === "SET_POSITION" &&
      event.x >= 0 &&
      event.x <= ctx.viewSize.width &&
      event.y >= 0 &&
      event.y <= ctx.viewSize.height
    );

    // console.log(`[isValidPos] Checks: x>=0? ${event.x >= 0}, x<=width? ${event.x <= ctx.viewSize.width}, y>=0? ${event.y >= 0}, y<=height? ${event.y <= ctx.viewSize.height}`);
    // console.log(`[isValidPos] Result: ${isValid}`);
    return isValid;
  }
  return false;
};

const isValidSize = (_: MediaMachineContext, event: ChildEvents) => {
  return event.type === "SET_SIZE" && event.width >= 0 && event.height >= 0;
};

const isValidSizeAndPosition = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (event.type === "SET_SIZE_AND_POSITION") {
    return (
      event.width >= 0 &&
      event.height >= 0 &&
      event.x >= 0 &&
      event.x <= ctx.viewSize.width &&
      event.y >= 0 &&
      event.y <= ctx.viewSize.height
    );
  }
  return false;
};

const isValidRotation = (_: MediaMachineContext, event: ChildEvents) => {
  return (
    event.type === "SET_ROTATION" && event.rotation >= 0 && event.rotation < 4
  );
};

/**
 * Sends to parent 'flow' machine an event to be added to it's context's undo stack
 * WORKAROUND: When using interpret() instead of spawn(), sendParent() fails
 * Wrap in try-catch or use parentSend callback if available
 */
const addToStack = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (ctx.parentSend) {
    ctx.parentSend({
      type: "ADD_UNDO_REDO",
      actions: ctx.sendAction,
    });
  } else {
    // Fallback: try sendParent (will work if spawned normally)
    try {
      return sendParent((ctx: MediaMachineContext, _: ChildEvents) => ({
        type: "ADD_UNDO_REDO",
        actions: ctx.sendAction,
      }))(ctx, event);
    } catch (e) {
      // console.warn('[addToStack] sendParent failed (expected with interpret()):', e);
    }
  }
};

/**
 * Sends to parent 'flow' machine an action to be committed to it's context's flow machine
 * WORKAROUND: When using interpret() instead of spawn(), sendParent() fails
 * Since ctx.mmo is a reference, Immer automatically propagates changes, so we don't need this anymore
 */
const commitToParent = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (ctx.parentSend) {
    ctx.parentSend({
      type: "COMMIT",
      mmo: ctx.mmo,
      id: ctx.mmo.id,
      shouldDraft: false,
      screenIndex: ctx.screenIndex
    });
  } else {
    // Fallback: try sendParent (will work if spawned normally)
    try {
      return sendParent((ctx: MediaMachineContext, _: ChildEvents) => ({
        type: "COMMIT",
        mmo: ctx.mmo,
        id: ctx.mmo.id,
        shouldDraft: false,
        screenIndex: ctx.screenIndex
      }))(ctx, event);
    } catch (e) {
      // console.warn('[commitToParent] sendParent failed (expected with interpret()):', e);
    }
  }
};

/**
 * Sends to parent 'flow' machine an action to be committed to it's context's flow machine
 * Also adds it to it's context's drafts list
 */
const commitToParentAndDraft = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (ctx.parentSend) {
    ctx.parentSend({
      type: "COMMIT",
      mmo: ctx.mmo,
      id: ctx.mmo.id,
      shouldDraft: true,
      screenIndex: ctx.screenIndex
    });
  } else {
    // Fallback: try sendParent (will work if spawned normally)
    try {
      return sendParent((ctx: MediaMachineContext, _: ChildEvents) => ({
        type: "COMMIT",
        mmo: ctx.mmo,
        id: ctx.mmo.id,
        shouldDraft: true,
        screenIndex: ctx.screenIndex
      }))(ctx, event);
    } catch (e) {
      // console.warn('[commitToParentAndDraft] sendParent failed (expected with interpret()):', e);
    }
  }
};

const logIdleEntry = (ctx: MediaMachineContext) => console.log(`[MediaMachine] ${ctx.mmo.id} entered IDLE state`);
const logActivate = (ctx: MediaMachineContext) => console.log(`[MediaMachine] ${ctx.mmo.id} received ACTIVATE, transitioning to active`);
const logActiveEntry = (ctx: MediaMachineContext) => console.log(`[MediaMachine] ${ctx.mmo.id} entered ACTIVE state`);

export const MediaMachine = Machine<
  MediaMachineContext,
  ChildMachineSchema,
  ChildEvents
>(
  {
    id: "child",
    initial: "active",  // TEMPORARY FIX: Start in active state
    strict: false,
    states: {
      active: {
        on: {
          ACTIVATE: {},
          DEACTIVATE: {},
          SET_POSITION: {
            actions: [
              "setPosition",
              commitToParent,
              addToStack
            ],
            // cond: isValidPos,  // TEMPORARILY REMOVED TO TEST
          },
          SET_SIZE: {
            actions: [
              "setSize",
              commitToParent,
              addToStack
            ],
            cond: isValidSize,
          },
          SET_SIZE_AND_POSITION: {
            actions: [
              "setSizeAndPosition",
              commitToParent,
              addToStack
            ],
            cond: isValidSizeAndPosition,
          },
          SET_ROTATION: {
            actions: ["setRotation", commitToParent, addToStack],
            cond: isValidRotation,
          },
          SET_ACTION: {
            actions: ["setAction", commitToParent, addToStack],
          },
          SET_COLOR: {
            actions: ["setColor", commitToParent, addToStack],
            cond: (ctx: MediaMachineContext, _) =>
              ctx.mmo.type === "text" || ctx.mmo.type === "shape",
          },
          SET_ALIGN: {
            actions: ["setAlign", commitToParent, addToStack],
            cond: isText,
          },
          SET_FONT_NAME: {
            actions: ["setFontName", commitToParent, addToStack],
            cond: isText,
          },
          SET_FONT_STYLE: {
            actions: ["setFontStyle", commitToParent, addToStack],
            cond: isText,
          },
          SET_FONT_SIZE: {
            actions: ["setFontSize", commitToParent, addToStack],
            cond: isText,
          },
          EDIT_TEXT: {
            actions: ["editText", commitToParentAndDraft, addToStack],
            cond: isText,
          },
          CHANGE_PREVIEW_IMG: {
            actions: ["changePreviewImage", commitToParentAndDraft, addToStack],
            cond: isAV,
          },
          SET_CROP: {
            actions: ["setCrop", commitToParentAndDraft, addToStack],
            cond: isAV,
          },
          SET_AUTOPLAY: {
            actions: ["toggleAutoplay", commitToParent, addToStack],
            cond: isVideo,
          },
          CHANGE_GALLERY_ASSET: {
            actions: ["changeGalleryAsset", commitToParentAndDraft, addToStack],
            cond: isGallery,
          },
          SET_CORNER_RADIUS: {
						actions: ['setCornerRadius', commitToParent, addToStack],
						cond: isAVOrShape,
					},
					SET_BORDER: {
						actions: ['setBorder', commitToParent, addToStack],
						cond: isAVOrShape,
					},
					SET_GRADIENT: {
						actions: ['setGradient', commitToParent, addToStack],
						cond: isShape,
					},

        },
      },
    },
  },
  {
    actions: {
      ...actions,
      logIdleEntry,
      logActivate,
      logActiveEntry,
    }
  }
);

// console.log('[MediaMachine] Registered actions:', Object.keys(MediaMachine.options.actions || {}));
